import { isFunction } from 'lodash';

export default class QueryBuilder
{
    constructor(execFn)
    {
        this._execFn = execFn;
        this._cursor = '';
        this._from = '';
        this._groups = [];
        this._includes = [];
        this._joins = [];
        this._limit = '';
        this._orders = [];
        this._query = '';
        this._validOperators = ['=', '<', '>', '<=', '>=', '<>', 'like', 'not like'];
        this._wheres = [];
        this._with = [];
    }

    buildQuery()
    {
        const finalIncludes = this._includes;

        this._with.forEach((w) =>
        {
            this._buildIncludes(w, finalIncludes, `${w.related}.`);
        });

        return {
            query: {
                from: this._from,
                wheres: this._wheres,
                with: this._with,
                groups: this._groups,
                orders: this._orders,
                joins: this._joins,
                limit: this._limit,
            },
            includes: finalIncludes,
            cursor: this._cursor,
        };
    }

    /**
     * @returns {Promise<HttpResponse>}
     */
    get()
    {
        return this._execFn(this);
    }

    groupBy(...groups)
    {
        groups.forEach((group) =>
        {
            this._groups = [...this._groups, ...group];
        });

        return this;
    }

    limit(limit)
    {
        this._limit = limit;

        return this;
    }

    orderBy(column, direction = 'asc')
    {
        this._orders.push({ column, direction });

        return this;
    }

    orWhere(column, operator = null, value = null)
    {
        return this.where(column, operator, value, 'or');
    }

    orWhereBetween(column, values)
    {
        return this.whereBetween(column, values, 'or');
    }

    orWhereDoesntHave(relation, callback = null)
    {
        return this.whereDoesntHave(relation, callback, 'or');
    }

    orWhereHas(relation, callback = null)
    {
        return this.whereHas(relation, callback, 'or');
    }

    orWhereIn(column, values)
    {
        return this.whereIn(column, values, 'or');
    }

    orWhereNotBetween(column, values)
    {
        return this.whereNotBetween(column, values, 'or');
    }

    orWhereNotIn(column, values)
    {
        return this.whereNotIn(column, values, 'or');
    }

    orWhereNotNull(column)
    {
        return this.whereNotNull(column, 'or');
    }

    orWhereNull(column)
    {
        return this.whereNull(column, 'or');
    }

    orWhereRaw(column, value = [])
    {
        return this.whereRaw(column, value, 'or');
    }

    /**
     *
     * @param column
     * @param operator
     * @param value
     * @param boolean
     * @returns {QueryBuilder}
     */
    where(column, operator = null, value = null, boolean = 'and')
    {
        if (isFunction(column))
        {
            const newQuery = new QueryBuilder();

            column(newQuery);

            return this.addNestedWhereQuery(newQuery, 'boolean');
        }

        let finalValue = value;
        let finalOperator = operator;
        const type = 'Basic';

        // If the given operator is not found in the list of valid operators we will
        // assume that the developer is just short-cutting the '=' operators and
        // we will set the operators to '=' and set the values appropriately.
        if (this._invalidOperator(finalOperator))
        {
            finalValue = finalOperator;
            finalOperator = '=';
        }

        this._wheres.push({
            type, column, operator: finalOperator, value: finalValue, boolean,
        });

        return this;
    }

    whereBetween(column, value, boolean = 'and', not = false)
    {
        const type = not ? 'NotBetween' : 'Between';

        this._wheres.push({
            column, value, type, boolean, not,
        });

        return this;
    }

    whereDoesntHave(relation, callback = null, boolean = 'and')
    {
        return this.whereHas(relation, callback, boolean, '<');
    }

    whereHas(relation, callback = null, boolean = 'and', operator = '>=', count = 1)
    {
        const type = 'Exists';

        const query = new QueryBuilder();

        query._from = relation;

        if (callback)
        {
            callback(query);
        }

        this._wheres.push({
            type, query: query.buildQuery().query, boolean, operator, count,
        });

        return this;
    }

    whereIn(column, value, boolean = 'and', not = false)
    {
        const type = not ? 'NotIn' : 'In';

        this._wheres.push({
            type, column, value, boolean,
        });

        return this;
    }

    whereNotBetween(column, values, boolean = 'and')
    {
        return this.whereBetween(column, values, boolean, true);
    }

    whereNotIn(column, values, boolean = 'and')
    {
        return this.whereIn(column, values, boolean, true);
    }

    whereNotNull(column, boolean = 'and')
    {
        return this.whereNull(column, boolean, true);
    }

    whereNull(column, boolean = 'and', not = false)
    {
        const type = not ? 'NotNull' : 'Null';

        this._wheres.push({ type, column, boolean });

        return this;
    }

    whereRaw(column, value = [], boolean = 'and')
    {
        const type = 'Raw';

        this._wheres.push({
            type, column, operator: null, value, boolean,
        });

        return this;
    }

    with(relations)
    {
        if (typeof relations === 'string')
        {
            this._includes.push(relations);
        }
        else if (Array.isArray(relations))
        {
            relations.forEach((relation) => this._includes.push(relation));
        }
        else if (typeof relations === 'object')
        {
            Object.keys(relations).forEach((key) =>
            {
                const query = new QueryBuilder();
                const type = 'with';
                const related = key;

                if (typeof relations[key] === 'function')
                {
                    relations[key](query);
                }

                this._includes.push(related);
                this._with.push({ type, query, related });
            });
        }

        return this;
    }

    withCursor(cursor)
    {
        this.cursor = cursor;

        return this;
    }

    addNestedWhereQuery(query, boolean = 'and')
    {
        if (query._wheres.length)
        {
            const type = 'Nested';

            this._wheres.push({ type, query: query.buildQuery().query, boolean });
        }

        return this;
    }

    // <editor-fold desc="Helpers">

    _buildIncludes(withRelated, finalIncludes, prefix = '')
    {
        withRelated.query._includes.forEach((withInclude) =>
        {
            finalIncludes.push(`${prefix}${withInclude}`);
        });

        withRelated.query._with.forEach((w) =>
        {
            this._buildIncludes(w, finalIncludes, `${prefix + w.related}.`);
        });
    }

    _invalidOperator(operator)
    {
        return typeof operator !== 'string' || this._validOperators.indexOf(operator.toLowerCase()) === -1;
    }

    // </editor-fold>
}
